import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

// LEAVE_TYPES moved inside component for i18n

const STATUS_COLORS = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  cancelled: '#6b7280',
};

export default function HRLeaves() {
  const { setPage, currentCompany } = useContext(AppContext);
  const [employees, setEmployees] = useState([]);
  const { t } = useI18n();
  const LEAVE_TYPES = {
    urlop_wypoczynkowy: { icon: '🏖️', label: t('hr_leaves.leave_urlop_wypoczynkowy') },
    urlop_bezplatny: { icon: '📋', label: t('hr_leaves.leave_urlop_bezplatny') },
    zwolnienie_lekarskie: { icon: '🏥', label: t('hr_leaves.leave_zwolnienie_lekarskie') },
    urlop_macierzynski: { icon: '👶', label: t('hr_leaves.leave_urlop_macierzynski') },
    urlop_ojcowski: { icon: '👨‍👶', label: t('hr_leaves.leave_urlop_ojcowski') },
    urlop_rodzicielski: { icon: '👪', label: t('hr_leaves.leave_urlop_rodzicielski') },
    urlop_okolicznosciowy: { icon: '💒', label: t('hr_leaves.leave_urlop_okolicznosciowy') },
    urlop_na_zadanie: { icon: '⚡', label: t('hr_leaves.leave_urlop_na_zadanie') },
  };
  const [leaves, setLeaves] = useState([]);
  const [balances, setBalances] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [showTerminate, setShowTerminate] = useState(null);
  const [tab, setTab] = useState('leaves'); // leaves | calendar | balances
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');

  const [form, setForm] = useState({
    employee_id: '', leave_type: 'urlop_wypoczynkowy', start_date: '', end_date: '', days: '', notes: ''
  });
  const [termForm, setTermForm] = useState({ termination_date: '', reason: '', notice_period: '' });
  const [termResult, setTermResult] = useState(null);

  useEffect(() => { load(); }, [currentCompany?.id]);

  async function load() {
    try {
      const cid = currentCompany?.id;
      const qs = cid ? `?company_id=${cid}` : '';
      const [empRes, leaveRes] = await Promise.all([
        api.get(`/hr/employees${qs}`),
        api.get(`/hr/leaves${qs}`),
      ]);
      setEmployees(empRes.data);
      setLeaves(leaveRes.data);

      // Load balances for all employees
      const bals = {};
      for (const emp of empRes.data) {
        try {
          const b = await api.get(`/hr/leaves/balance/${emp.id}`);
          bals[emp.id] = b.data;
        } catch {}
      }
      setBalances(bals);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error loading data');
    } finally {
      setLoading(false);
    }
  }

  async function createLeave() {
    try {
      setError(null);
      await api.post('/hr/leaves', {
        ...form,
        employee_id: parseInt(form.employee_id),
        days: parseInt(form.days),
      });
      setShowForm(false);
      setForm({ employee_id: '', leave_type: 'urlop_wypoczynkowy', start_date: '', end_date: '', days: '', notes: '' });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
  }

  async function approveLeave(id, action) {
    try {
      await api.patch(`/hr/leaves/${id}/approve`, { action });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
  }

  async function cancelLeave(id) {
    try {
      await api.delete(`/hr/leaves/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
  }

  async function terminateEmployee() {
    if (!showTerminate) return;
    try {
      setError(null);
      const res = await api.post(`/hr/employees/${showTerminate.id}/terminate`, termForm);
      setTermResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error');
    }
  }

  const filteredLeaves = filterStatus ? leaves.filter(l => l.status === filterStatus) : leaves;

  // Calendar: current month grid
  function CalendarView() {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const monthLeaves = leaves.filter(l => l.status === 'approved' && {
      start: new Date(l.start_date), end: new Date(l.end_date)
    }).filter(l => {
      const s = new Date(l.start_date);
      const e = new Date(l.end_date);
      return s.getMonth() <= month && e.getMonth() >= month && s.getFullYear() <= year && e.getFullYear() >= year;
    });

    const empIds = [...new Set(monthLeaves.map(l => l.employee_id))];
    const empMap = {};
    employees.forEach(e => { empMap[e.id] = e; });

    return (
      <div style={{ overflowX: 'auto' }}>
        <h3>📅 {now.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })}</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ padding: 4, textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>{t('hr_leaves.employee')}</th>
              {Array.from({ length: daysInMonth }, (_, i) => (
                <th key={i} style={{
                  padding: 2, textAlign: 'center', minWidth: 24,
                  borderBottom: '1px solid rgba(255,255,255,0.2)',
                  color: [0, 6].includes(new Date(year, month, i + 1).getDay()) ? '#ef4444' : 'inherit'
                }}>{i + 1}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(empIds.length > 0 ? empIds : employees.map(e => e.id)).map(empId => {
              const emp = empMap[empId];
              if (!emp) return null;
              const empLeaves = monthLeaves.filter(l => l.employee_id === empId);
              return (
                <tr key={empId}>
                  <td style={{ padding: 4, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    {emp.first_name} {emp.last_name}
                  </td>
                  {Array.from({ length: daysInMonth }, (_, i) => {
                    const d = new Date(year, month, i + 1);
                    const isWeekend = [0, 6].includes(d.getDay());
                    const ds = d.toISOString().split('T')[0];
                    const leave = empLeaves.find(l => ds >= l.start_date && ds <= l.end_date);
                    return (
                      <td key={i} style={{
                        padding: 2, textAlign: 'center',
                        background: leave
                          ? (leave.leave_type === 'zwolnienie_lekarskie' ? 'rgba(239,68,68,0.3)' : 'rgba(99,102,241,0.3)')
                          : isWeekend ? 'rgba(255,255,255,0.03)' : 'transparent',
                        borderBottom: '1px solid rgba(255,255,255,0.05)',
                        fontSize: 10,
                      }}>
                        {leave ? (LEAVE_TYPES[leave.leave_type]?.icon || '🔵') : ''}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (loading) return <div className="page"><div className="spinner" /></div>;

  return (
    <div className="page">
      <button className="btn btn-link back-btn" onClick={() => setPage('hr')}>← {t('common.back')}</button>
      <h1>🏖️ {t('hr_leaves.title')}</h1>

      {error && <div className="banner banner-warning" style={{ marginBottom: 12 }}>⚠️ {error}
        <button style={{ marginLeft: 8, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }} onClick={() => setError(null)}>✕</button>
      </div>}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'leaves', icon: '📋', label: t('hr_leaves.tab_requests') },
          { id: 'calendar', icon: '📅', label: t('hr_leaves.tab_calendar') },
          { id: 'balances', icon: '📊', label: t('hr_leaves.tab_balances') },
        ].map(opt => (
          <button key={opt.id} className={`btn ${tab === opt.id ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setTab(opt.id)} style={{ fontSize: 13 }}>
            {opt.icon} {opt.label}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ {t('hr_leaves.new_request')}</button>
      </div>

      {/* Leave Request Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h3>➕ {t('hr_leaves.new_leave_request')}</h3>
          <div className="form">
            <div className="field">
              <label>{t('hr_leaves.employee')} *</label>
              <select value={form.employee_id} onChange={e => setForm(p => ({ ...p, employee_id: e.target.value }))}>
                <option value="">{t('hr_leaves.select_employee')}</option>
                {employees.filter(e => e.status === 'active').map(e => (
                  <option key={e.id} value={e.id}>{e.first_name} {e.last_name}</option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('hr_leaves.leave_type')} *</label>
              <select value={form.leave_type} onChange={e => setForm(p => ({ ...p, leave_type: e.target.value }))}>
                {Object.entries(LEAVE_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v.icon} {v.label}</option>
                ))}
              </select>
            </div>
            <div className="field"><label>{t('hr_leaves.date_from')} *</label><input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} /></div>
            <div className="field"><label>{t('hr_leaves.date_to')} *</label><input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} /></div>
            <div className="field"><label>{t('hr_leaves.work_days')} *</label><input type="number" min="1" value={form.days} onChange={e => setForm(p => ({ ...p, days: e.target.value }))} /></div>
            <div className="field"><label>{t('hr_leaves.notes')}</label><input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} /></div>
          </div>
          {form.employee_id && balances[parseInt(form.employee_id)] && (
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 8 }}>
              💡 {t('hr_leaves.remaining_info')}: {balances[parseInt(form.employee_id)].leave_days_remaining} {t('hr_leaves.days_vacation')},
              {' '}{balances[parseInt(form.employee_id)].urlop_na_zadanie_remaining} {t('hr_leaves.days_on_demand')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="btn btn-primary" onClick={createLeave}>✅ {t('hr_leaves.submit_request')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* Leaves List Tab */}
      {tab === 'leaves' && (
        <>
          <div style={{ marginBottom: 12, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {['', 'pending', 'approved', 'rejected', 'cancelled'].map(s => (
              <button key={s} className={`btn ${filterStatus === s ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: 12, padding: '4px 10px' }}
                onClick={() => setFilterStatus(s)}>
                {s || t('hr_leaves.filter_all')} {s && `(${leaves.filter(l => l.status === s).length})`}
              </button>
            ))}
          </div>
          <div className="list">
            {filteredLeaves.map(l => (
              <div key={l.id} className="list-item" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="list-main" style={{ flex: 1 }}>
                  <div className="list-title">
                    {LEAVE_TYPES[l.leave_type]?.icon} {l.employee_name} — {LEAVE_TYPES[l.leave_type]?.label || l.leave_type}
                  </div>
                  <div className="list-sub">
                    {l.start_date} → {l.end_date} ({l.days} dni) {l.notes && `• ${l.notes}`}
                  </div>
                </div>
                <span style={{
                  padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 600,
                  background: `${STATUS_COLORS[l.status]}22`, color: STATUS_COLORS[l.status],
                }}>{l.status}</span>
                {l.status === 'pending' && (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-primary btn-small" style={{ fontSize: 11, padding: '2px 8px' }}
                      onClick={() => approveLeave(l.id, 'approve')}>✅</button>
                    <button className="btn btn-secondary btn-small" style={{ fontSize: 11, padding: '2px 8px' }}
                      onClick={() => approveLeave(l.id, 'reject')}>❌</button>
                    <button className="btn btn-secondary btn-small" style={{ fontSize: 11, padding: '2px 8px' }}
                      onClick={() => cancelLeave(l.id)}>🗑️</button>
                  </div>
                )}
              </div>
            ))}
            {filteredLeaves.length === 0 && <div className="empty">{t('hr_leaves.no_requests')}</div>}
          </div>
        </>
      )}

      {/* Calendar Tab */}
      {tab === 'calendar' && <CalendarView />}

      {/* Balances Tab */}
      {tab === 'balances' && (
        <div className="list">
          {employees.filter(e => e.status === 'active').map(emp => {
            const b = balances[emp.id];
            return (
              <div key={emp.id} className="list-item">
                <div className="list-main" style={{ flex: 1 }}>
                  <div className="list-title">👤 {emp.first_name} {emp.last_name}</div>
                  {b ? (
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, marginTop: 4, flexWrap: 'wrap' }}>
                      <span>🏖️ Wypoczynkowy: <b style={{ color: '#10b981' }}>{b.leave_days_remaining}</b>/{b.leave_days_total}</span>
                      <span>⚡ Na żądanie: <b>{b.urlop_na_zadanie_remaining}</b>/4</span>
                      <span>🏥 L4: <b>{b.sick_days}</b> dni ({b.sick_employer_days} pracodawca, {b.sick_zus_days} ZUS)</span>
                      {Object.entries(b.by_type || {}).filter(([k]) => !['urlop_wypoczynkowy', 'urlop_na_zadanie', 'zwolnienie_lekarskie'].includes(k)).map(([k, v]) => (
                        <span key={k}>{LEAVE_TYPES[k]?.icon} {v.label}: <b>{v.days}</b> dni</span>
                      ))}
                    </div>
                  ) : <div className="list-sub">{t('hr_leaves.loading_data')}</div>}
                </div>
                <button className="btn btn-secondary btn-small" style={{ fontSize: 11, color: '#ef4444' }}
                  onClick={() => { setShowTerminate(emp); setTermResult(null); setTermForm({ termination_date: '', reason: '', notice_period: '' }); }}>
                  🔴 {t('hr_leaves.dismiss_btn')}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Termination Dialog */}
      {showTerminate && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} onClick={() => { setShowTerminate(null); setTermResult(null); }}>
          <div className="card" style={{ padding: 20, maxWidth: 500, width: '90%' }} onClick={e => e.stopPropagation()}>
            {!termResult ? (
              <>
                <h3>🔴 {t('hr_leaves.termination_title')}: {showTerminate.first_name} {showTerminate.last_name}</h3>
                <div className="form">
                  <div className="field"><label>{t('hr_leaves.termination_date')} *</label><input type="date" value={termForm.termination_date} onChange={e => setTermForm(p => ({ ...p, termination_date: e.target.value }))} /></div>
                  <div className="field"><label>{t('hr_leaves.reason')} *</label><input value={termForm.reason} onChange={e => setTermForm(p => ({ ...p, reason: e.target.value }))} /></div>
                  <div className="field"><label>{t('hr_leaves.notice_period')}</label><input value={termForm.notice_period} placeholder={t('hr_leaves.notice_placeholder')} onChange={e => setTermForm(p => ({ ...p, notice_period: e.target.value }))} /></div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                  <button className="btn" style={{ background: '#ef4444', color: '#fff' }} onClick={terminateEmployee}>🔴 {t('hr_leaves.confirm_termination')}</button>
                  <button className="btn btn-secondary" onClick={() => setShowTerminate(null)}>{t('common.cancel')}</button>
                </div>
              </>
            ) : (
              <>
                <h3>✅ {t('hr_leaves.terminated_success')}</h3>
                <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                  <div>📅 {t('hr_leaves.term_date')}: <b>{termResult.termination_date}</b></div>
                  <div>📝 {t('hr_leaves.term_reason')}: {termResult.reason}</div>
                  <div>📄 {t('hr_leaves.contracts_terminated')}: {termResult.contracts_terminated}</div>
                  <div style={{ marginTop: 8, padding: 10, borderRadius: 8, background: 'rgba(99,102,241,0.15)' }}>
                    <div style={{ fontWeight: 600 }}>💰 {t('hr_leaves.leave_equivalent')}</div>
                    <div>{t('hr_leaves.remaining_days')}: {termResult.ekwiwalent_za_urlop?.days}</div>
                    <div>{t('hr_leaves.daily_rate')}: {termResult.ekwiwalent_za_urlop?.daily_rate?.toLocaleString()} PLN</div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{t('hr_leaves.to_pay')}: {termResult.ekwiwalent_za_urlop?.total?.toLocaleString()} PLN</div>
                  </div>
                </div>
                <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => { setShowTerminate(null); setTermResult(null); load(); }}>OK</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
